import { SrCustomerStatus } from '@plato/data/starrocks/types';
import { GlobalSearchTab, SearchEntity, SearchEntityTab } from '@plato/search/search-types';
import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardMainAction,
    CardOpenIcon,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { DialogIconClose } from '@plato/ui/containers/dialog';
import { Join } from '@plato/ui/containers/join';
import { Button } from '@plato/ui/primitives/button';
import { Highlight } from '@plato/ui/primitives/highlight';
import { Tab, TabBar, Tabs } from '@plato/ui/primitives/tab-bar';
import { cn } from '@plato/ui/util/classes';
import {
    ChevronLeftIcon,
    FileIcon,
    HashIcon,
    MapPinIcon,
    PackageIcon,
    ShoppingBagIcon,
    StoreIcon,
} from 'lucide-react';
import type { ComponentType, ReactElement, ReactNode, Ref } from 'react';
import { Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { safeJoin } from '../../../utils/string-utils.ts';
import { Search, SearchInput, SearchResults } from '../../shared/components/search/search.tsx';
import {
    useSearchEntityData,
    type SearchEntityItem,
    type SearchFieldIcon,
    type SearchTracking,
} from '../../shared/hooks/use-search-entity.ts';

export type GlobalSearchContentProps = {
    entities: SearchEntity[];
    inputPlaceholder?: string;
    inputRef?: Ref<HTMLInputElement>;
    onItemSelected?: (item: SearchEntityItem) => void;
    initialTab?: GlobalSearchTab;
    // Without the "all" tab, search opens on the first entity's tab.
    hideAllTab?: boolean;
    tracking: SearchTracking;
};

const ENTITY_ICONS: Record<SearchEntity, ComponentType<{ className?: string }>> = {
    [SearchEntity.Customer]: StoreIcon,
    [SearchEntity.SalesOrder]: ShoppingBagIcon,
    [SearchEntity.SalesQuote]: FileIcon,
    [SearchEntity.Product]: PackageIcon,
};

const META_FIELD_ICONS: Record<SearchFieldIcon, ComponentType<{ className?: string }>> = {
    id: HashIcon,
    location: MapPinIcon,
    customer: StoreIcon,
};

const groupCap = 3;

const NON_ACTIVE_STATUS_COLOR: Record<string, 'text-brand' | 'text-warning' | 'text-danger'> = {
    [SrCustomerStatus.NEW]: 'text-brand',
    [SrCustomerStatus.AT_RISK]: 'text-warning',
    [SrCustomerStatus.INACTIVE]: 'text-danger',
    [SrCustomerStatus.DORMANT]: 'text-danger',
    [SrCustomerStatus.BLOCKED]: 'text-danger',
};

function ResultRow({
    item,
    onSelect,
}: {
    item: SearchEntityItem;
    onSelect: (item: SearchEntityItem) => void;
}): ReactElement {
    const { t } = useTranslation();
    const { entity, name, subtitle, caption, status } = item;
    const Icon = ENTITY_ICONS[entity];

    const captionText = item.highlight?.caption?.text ?? caption?.text;
    const CaptionIcon = caption?.icon ? META_FIELD_ICONS[caption.icon] : undefined;
    const subtitleText = item.highlight?.subtitle?.text ?? subtitle?.text;
    const SubtitleIcon = subtitle?.icon ? META_FIELD_ICONS[subtitle.icon] : undefined;
    const match = item.highlight?.match;
    const nonActiveStatus =
        entity === SearchEntity.Customer && status && status !== SrCustomerStatus.ACTIVE ? status : undefined;

    const nonActiveStatusLabel = (): string => {
        switch (nonActiveStatus) {
            case SrCustomerStatus.NEW:
                return t('status_new', { ns: 'common' });
            case SrCustomerStatus.AT_RISK:
                return t('status_at_risk', { ns: 'common' });
            case SrCustomerStatus.INACTIVE:
                return t('status_inactive', { ns: 'common' });
            case SrCustomerStatus.DORMANT:
                return t('status_dormant', { ns: 'common' });
            case SrCustomerStatus.BLOCKED:
                return t('status_blocked', { ns: 'common' });
            default:
                return '';
        }
    };

    // The row is one option, so compose its accessible name from every visible line —
    // the status otherwise exists only as a colored dot.
    const accessibleName = safeJoin(
        [
            item.highlight?.name?.text ?? name,
            match ? (match.prefix ? `${match.prefix}: ${match.text}` : match.text) : undefined,
            captionText,
            subtitleText,
            nonActiveStatus ? nonActiveStatusLabel() : undefined,
        ],
        ', ',
    );

    return (
        // level 3: Card's box chrome is l2-gated, so a nested level renders the bare
        // list row the design calls for — the hairline comes from the Join's divide-y.
        <Card level={3} role="option" aria-selected={false} aria-label={accessibleName}>
            <CardFront>
                <CardStart>
                    <Icon className="size-4" />
                </CardStart>
                <CardContent className="min-w-0">
                    <CardTitle className="min-w-0">
                        <Highlight className="truncate" text={item.highlight?.name?.text ?? name} />
                    </CardTitle>
                    {match && (
                        <CardDetail className="truncate">
                            <Highlight text={match.prefix ? `${match.prefix}: ${match.text}` : match.text} />
                        </CardDetail>
                    )}
                    {(captionText || subtitleText || nonActiveStatus) && (
                        <CardSubtitle className="shrink gap-default">
                            {captionText && (
                                <span className="flex min-w-0 items-center gap-tight">
                                    {CaptionIcon && <CaptionIcon className="size-3 shrink-0" />}
                                    <span className="truncate">
                                        <Highlight text={captionText} />
                                    </span>
                                </span>
                            )}
                            {subtitleText && (
                                <span className="flex min-w-0 items-center gap-tight">
                                    {SubtitleIcon && <SubtitleIcon className="size-3 shrink-0" />}
                                    <span className="truncate">
                                        <Highlight text={subtitleText} />
                                    </span>
                                </span>
                            )}
                            {nonActiveStatus && (
                                <span
                                    className={cn(
                                        'flex shrink-0 items-center gap-tight',
                                        NON_ACTIVE_STATUS_COLOR[nonActiveStatus],
                                    )}
                                >
                                    <span className="size-1.5 shrink-0 rounded-full bg-current" />
                                    <span className="truncate">{nonActiveStatusLabel()}</span>
                                </span>
                            )}
                        </CardSubtitle>
                    )}
                </CardContent>
                <CardMainAction
                    // CardMainAction sets `flex` but no align-items, so the icon would
                    // otherwise sit at the top of its full-height box.
                    className="items-center"
                    onClick={() => {
                        onSelect(item);
                    }}
                >
                    <CardOpenIcon />
                </CardMainAction>
            </CardFront>
        </Card>
    );
}

export function GlobalSearchContent({
    entities,
    onItemSelected,
    inputPlaceholder,
    inputRef,
    initialTab,
    hideAllTab,
    tracking,
}: GlobalSearchContentProps): ReactNode {
    const { t } = useTranslation();
    const getSearchTabLabel = (tab: string): string => {
        switch (tab) {
            case GlobalSearchTab.All:
                return t('search__label__all', { ns: 'common' });
            case GlobalSearchTab.Customers:
                return t('search__label__customers', { ns: 'common' });
            case GlobalSearchTab.Orders:
                return t('search__label__sales_orders', { ns: 'common' });
            case GlobalSearchTab.Products:
                return t('search__label__products', { ns: 'common' });
            case GlobalSearchTab.Quotes:
                return t('search__label__sales_quotes', { ns: 'common' });
            default:
                return tab;
        }
    };

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const listRef = useRef<HTMLDivElement>(null);

    const scrollListToTheTop = (): void => {
        requestAnimationFrame(() => {
            listRef.current?.scrollTo({ top: 0 });
        });
    };

    const [selectedTabKey, setSelectedTabKey] = useState<GlobalSearchTab>(() => {
        if (initialTab) {
            return initialTab;
        }
        return (entities.length === 1 || hideAllTab) && entities[0]
            ? SearchEntityTab[entities[0]]
            : GlobalSearchTab.All;
    });

    const entityTabs = entities.map((entity) => SearchEntityTab[entity]);
    const tabs = entities.length > 1 ? (hideAllTab ? entityTabs : [GlobalSearchTab.All, ...entityTabs]) : [];

    useDebounce(
        () => {
            setDebouncedSearch(search);
            scrollListToTheTop();
        },
        250,
        [search],
    );

    const { data, isLoading, isFetched, trackResultClicked } = useSearchEntityData({
        entities,
        search: debouncedSearch,
        selectedTabKey,
        tracking,
    });

    const handleItemSelected = (item: SearchEntityItem): void => {
        trackResultClicked(item);
        onItemSelected?.(item);
    };

    const entitiesWithResults = [...data.entries()].filter(([, items]) => items.length);
    const hasResults = entitiesWithResults.length > 0;

    return (
        <Search query={search} onQueryChange={setSearch}>
            <div
                className={cn(
                    'flex shrink-0 items-center gap-cozy px-container-relaxed pt-container-relaxed',
                    search ? 'pb-container-cozy' : 'pb-container-relaxed',
                )}
                // data-container: de-emphasizes the back button via l2l3, instead of
                // overriding its background by hand.
                data-container="2"
            >
                <SearchInput
                    ref={inputRef}
                    placeholder={inputPlaceholder || t('top_navbar__search-placeholder')}
                    clearLabel={t('search__clear', { ns: 'common' })}
                />
                {/* Last in the DOM so the input keeps initialFocus's first-tabbable slot,
                    first visually via order-first. The border survives the l2l3 wash. */}
                <DialogIconClose
                    className="order-first border border-line-weak sm:hidden"
                    aria-label={t('back', { ns: 'common' })}
                >
                    <ChevronLeftIcon />
                </DialogIconClose>
            </div>
            {!!search && tabs.length > 0 && (
                <Tabs value={selectedTabKey} onValueChange={setSelectedTabKey} className="shrink-0">
                    <TabBar className="px-container-relaxed">
                        {tabs.map((tabId) => (
                            <Tab key={tabId} value={tabId}>
                                {getSearchTabLabel(tabId)}
                            </Tab>
                        ))}
                    </TabBar>
                </Tabs>
            )}
            {!!search && (
                <SearchResults
                    ref={listRef}
                    loading={isLoading}
                    empty={isFetched && !isLoading && !hasResults}
                    emptyMessage={t('nothing_found')}
                >
                    {entitiesWithResults.map(([entity, items]) => {
                        const isAll = selectedTabKey === GlobalSearchTab.All;
                        const capped = isAll ? items.slice(0, groupCap) : items;
                        const hidden = isAll ? items.length - capped.length : 0;

                        const rows = (
                            <Join
                                orientation="vertical"
                                // data-container: rows are level 3, so the show-more Button
                                // de-emphasizes to transparent-with-wash, not a filled pill.
                                data-container="3"
                                className="divide-y divide-line-weakest"
                            >
                                {capped.map((item) => (
                                    <ResultRow key={item.id} item={item} onSelect={handleItemSelected} />
                                ))}
                                {hidden > 0 && (
                                    <Button
                                        variant="secondary"
                                        role="option"
                                        aria-selected={false}
                                        // l2l3-prefixed so it beats the variant's own l2l3:text rule.
                                        className="mt-tight justify-start px-container-cozy l2l3:text-brand"
                                        onClick={() => {
                                            setSelectedTabKey(SearchEntityTab[entity]);
                                        }}
                                    >
                                        {t('show_more')}
                                    </Button>
                                )}
                            </Join>
                        );

                        if (!isAll) {
                            return <Fragment key={entity}>{rows}</Fragment>;
                        }

                        const label = getSearchTabLabel(SearchEntityTab[entity]);

                        return (
                            <div
                                key={entity}
                                role="group"
                                aria-label={label}
                                className="flex flex-col gap-container-cozy"
                            >
                                <span className="text-subtle-heading">{label}</span>
                                {rows}
                            </div>
                        );
                    })}
                </SearchResults>
            )}
        </Search>
    );
}
